import { useState, useEffect } from 'react'
import { 
  Mail, 
  Lock,
  ArrowRight, 
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff,
  ShieldCheck,
  X,
  LogOut,
  TrendingUp,
  BarChart3,
  PieChart,
  Users,
  Activity
} from 'lucide-react'

const LoginComponent = ({ onLogin }) => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [failedAttempts, setFailedAttempts] = useState(0)
  const [isLocked, setIsLocked] = useState(false)
  const [lockoutTime, setLockoutTime] = useState(null)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState('error')
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [currentUser, setCurrentUser] = useState('')
  
  // Security constants
  const MAX_FAILED_ATTEMPTS = 3
  const LOCKOUT_DURATION = 5 * 60 * 1000
  const SESSION_TIMEOUT = 15 * 60 * 1000
  
  // Obfuscated credentials
  const VP = atob('VkRhcnRAMjAyNFNlY3VyZSE=')
  const AE = [
    atob('cnVremFuYWZhdGhpbWEub0B2ZGFydGluYy5jb20='),
    atob('aWJyYWhpbUB2ZGFydGluYy5jb20='),
    atob('bHluLmdAdmRhcnRpbmMuY29t'),
    atob('YWJzYW5hLmFAdmRhcnRpbmMuY29t'),
    atob('bGVvLnNAdmRhcnRpbmMuY29t'),
    atob('c3VnaWwuc0B2ZGFydGluYy5jb20='),
    atob('ZC5zaWRAdmRhcnRpbmMuY29t'),
    atob('b2xpdmVyQHZkYXJ0aW5jLmNvbQ=='),
      atob('c3VyZXNoLm5AdmRhcnRpbmMuY29t') 
  ]

  useEffect(() => {
    // Check for existing session
    const checkSession = () => {
      const session = sessionStorage.getItem('vdartSession')
      if (session) {
        try {
          const sessionData = JSON.parse(atob(session))
          if (sessionData.expires > Date.now()) {
            setIsLoggedIn(true)
            setCurrentUser(sessionData.email)
            onLogin(sessionData.email)
          } else {
            sessionStorage.removeItem('vdartSession')
          }
        } catch (e) {
          sessionStorage.removeItem('vdartSession')
        }
      }
    }

    checkSession()

    // Check for lockout
    const storedAttempts = localStorage.getItem('fla')
    if (storedAttempts) {
      setFailedAttempts(parseInt(storedAttempts))
    }
    
    const lockedUntil = localStorage.getItem('alu')
    if (lockedUntil) {
      const lockTime = parseInt(lockedUntil)
      if (Date.now() < lockTime) {
        setIsLocked(true)
        setLockoutTime(lockTime)
        
        const timer = setInterval(() => {
          if (Date.now() >= lockTime) {
            setIsLocked(false)
            setLockoutTime(null)
            localStorage.removeItem('alu')
            setFailedAttempts(0)
            localStorage.removeItem('fla')
            clearInterval(timer)
          }
        }, 1000)
        
        return () => clearInterval(timer)
      } else {
        localStorage.removeItem('alu')
        localStorage.removeItem('fla')
      }
    }

    // Set up activity monitoring
    const resetTimer = () => {
      const session = sessionStorage.getItem('vdartSession')
      if (session) {
        const sessionData = JSON.parse(atob(session))
        sessionData.expires = Date.now() + SESSION_TIMEOUT
        sessionStorage.setItem('vdartSession', btoa(JSON.stringify(sessionData)))
      }
    }

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart']
    events.forEach(event => {
      document.addEventListener(event, resetTimer)
    })

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, resetTimer)
      })
    }
  }, [onLogin])

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const isAuthorized = (email) => {
    return AE.includes(email.toLowerCase())
  }

  const showNotification = (message, type = 'error') => {
    setToastMessage(message)
    setToastType(type)
    setShowToast(true)
    setTimeout(() => setShowToast(false), 5000)
  }

  const sanitizeInput = (input) => {
    return input.replace(/[<>]/g, '')
  }

  const handleLogin = async (e) => {
    e.preventDefault()
    
    if (isLocked) {
      const remainingTime = Math.ceil((lockoutTime - Date.now()) / 1000 / 60)
      showNotification(`Account locked. Try again in ${remainingTime} minutes.`, 'error')
      return
    }

    setError('')
    
    if (!email || !password) {
      setError('Please enter both email and password')
      return
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address')
      return
    }

    setLoading(true)

    setTimeout(() => {
      if (!isAuthorized(email)) {
        setLoading(false)
        showNotification('Unauthorized: Access denied', 'error')
        setError('Invalid credentials')
        return
      }

      if (password === VP) {
        setFailedAttempts(0)
        localStorage.removeItem('fla')
        
        const sessionData = {
          email,
          timestamp: Date.now(),
          expires: Date.now() + SESSION_TIMEOUT
        }
        
        sessionStorage.setItem('vdartSession', btoa(JSON.stringify(sessionData)))
        setIsLoggedIn(true)
        setCurrentUser(email)
        showNotification('Login successful!', 'success')
        
        setTimeout(() => {
          onLogin(email)
        }, 500)
      } else {
        const newFailedAttempts = failedAttempts + 1
        setFailedAttempts(newFailedAttempts)
        localStorage.setItem('fla', newFailedAttempts.toString())
        
        if (newFailedAttempts >= MAX_FAILED_ATTEMPTS) {
          const lockUntil = Date.now() + LOCKOUT_DURATION
          localStorage.setItem('alu', lockUntil.toString())
          setIsLocked(true)
          setLockoutTime(lockUntil)
          showNotification('Account locked due to multiple failed attempts', 'error')
        } else {
          setError(`Invalid credentials. ${MAX_FAILED_ATTEMPTS - newFailedAttempts} attempts remaining`)
        }
      }
      
      setLoading(false)
    }, 1000)
  }

  const handleLogout = () => {
    sessionStorage.removeItem('vdartSession')
    setIsLoggedIn(false)
    setCurrentUser('')
    setEmail('')
    setPassword('')
    showNotification('Logged out successfully', 'success')
  }

  const styles = `
    * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }

    body {
      margin: 0;
      padding: 0;
      overflow-x: hidden;
    }

    .page-container {
      min-height: 100vh;
      background: #0f172a;
      display: flex;
      position: relative;
      overflow: hidden;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }

    /* Background Pattern */
    .bg-pattern {
      position: absolute;
      inset: 0;
      background-image: 
        radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
        radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.1) 0%, transparent 50%),
        radial-gradient(circle at 40% 20%, rgba(34, 197, 94, 0.05) 0%, transparent 50%);
    }

    .grid-overlay {
      position: absolute;
      inset: 0;
      background-image: 
        linear-gradient(rgba(255, 255, 255, 0.01) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255, 255, 255, 0.01) 1px, transparent 1px);
      background-size: 50px 50px;
    }

    /* Left Panel - Dashboard Preview */
    .left-panel {
      flex: 1;
      padding: 3rem;
      display: flex;
      flex-direction: column;
      justify-content: center;
      position: relative;
      z-index: 1;
      min-width: 50%;
    }

    .dashboard-preview {
      max-width: 500px;
    }

    .preview-header {
      margin-bottom: 2.5rem;
    }

    .preview-logo {
      height: 32px;
      margin-bottom: 1.5rem;
      filter: brightness(0) invert(1);
    }

    .preview-title {
      font-size: 2.5rem;
      font-weight: 800;
      color: white;
      margin-bottom: 1rem;
      line-height: 1.1;
    }

    .preview-subtitle {
      font-size: 1rem;
      color: #94a3b8;
      line-height: 1.6;
    }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 1rem;
      margin-bottom: 2rem;
    }

    .stat-card {
      background: rgba(255, 255, 255, 0.05);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 12px;
      padding: 1.25rem;
      transition: all 0.3s ease;
    }

    .stat-card:hover {
      background: rgba(255, 255, 255, 0.08);
      transform: translateY(-2px);
    }

    .stat-icon {
      width: 32px;
      height: 32px;
      padding: 6px;
      border-radius: 8px;
      margin-bottom: 0.75rem;
    }

    .stat-icon.blue {
      background: rgba(59, 130, 246, 0.2);
      color: #60a5fa;
    }

    .stat-icon.green {
      background: rgba(34, 197, 94, 0.2);
      color: #4ade80;
    }

    .stat-icon.purple {
      background: rgba(139, 92, 246, 0.2);
      color: #a78bfa;
    }

    .stat-icon.orange {
      background: rgba(251, 146, 60, 0.2);
      color: #fb923c;
    }

    .stat-value {
      font-size: 1.75rem;
      font-weight: 700;
      color: white;
      margin-bottom: 0.25rem;
    }

    .stat-label {
      font-size: 0.8rem;
      color: #94a3b8;
    }

    .features {
      display: flex;
      gap: 1.5rem;
      margin-top: 2.5rem;
    }

    .feature {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: #94a3b8;
      font-size: 0.875rem;
    }

    .feature-icon {
      width: 18px;
      height: 18px;
      color: #60a5fa;
    }

    /* Right Panel - Login Form */
    .right-panel {
      flex: 1;
      min-width: 50%;
      background: white;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 2rem;
      position: relative;
      z-index: 1;
    }

    .login-wrapper {
      width: 100%;
      max-width: 320px;
    }

    .login-header {
      text-align: center;
      margin-bottom: 2rem;
    }

    .login-title {
      font-size: 1.75rem;
      font-weight: 700;
      color: #1e293b;
      margin-bottom: 0.5rem;
    }

    .login-subtitle {
      color: #64748b;
      font-size: 0.875rem;
    }

    .security-badge {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.375rem;
      background: #f0f9ff;
      padding: 0.5rem 1rem;
      border-radius: 6px;
      margin-bottom: 1.5rem;
      font-size: 0.75rem;
      color: #0369a1;
    }

    .form-group {
      margin-bottom: 1rem;
    }

    .label {
      display: block;
      font-size: 0.8125rem;
      font-weight: 600;
      color: #1e293b;
      margin-bottom: 0.375rem;
    }

    .input-wrapper {
      position: relative;
    }

    .input {
      width: 100%;
      padding: 0.625rem 0.875rem 0.625rem 2.5rem;
      font-size: 0.875rem;
      border: 2px solid #e2e8f0;
      border-radius: 8px;
      outline: none;
      transition: all 0.2s ease;
      background: #f8fafc;
    }

    .input:focus {
      border-color: #3b82f6;
      background: white;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    .input.error {
      border-color: #ef4444;
    }

    .input:disabled {
      background: #f1f5f9;
      cursor: not-allowed;
      opacity: 0.7;
    }

    .input-icon {
      position: absolute;
      left: 0.875rem;
      top: 50%;
      transform: translateY(-50%);
      color: #64748b;
      width: 16px;
      height: 16px;
    }

    .password-toggle {
      position: absolute;
      right: 0.875rem;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      cursor: pointer;
      color: #64748b;
      padding: 0.25rem;
    }

    .password-toggle:hover {
      color: #334155;
    }

    .error-message {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      background: #fef2f2;
      border: 1px solid #fee2e2;
      border-radius: 6px;
      padding: 0.625rem 0.75rem;
      margin-top: 0.75rem;
      font-size: 0.8125rem;
      color: #b91c1c;
    }

    .button {
      width: 100%;
      padding: 0.75rem 1.25rem;
      font-size: 0.875rem;
      font-weight: 600;
      border-radius: 8px;
      border: none;
      cursor: pointer;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
      color: white;
      margin-top: 1.5rem;
      box-shadow: 0 4px 14px rgba(59, 130, 246, 0.3);
    }

    .button:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
    }

    .button:active:not(:disabled) {
      transform: translateY(0);
    }

    .button:disabled {
      background: #94a3b8;
      cursor: not-allowed;
      box-shadow: none;
    }

    .spinner {
      width: 14px;
      height: 14px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-top-color: white;
      border-radius: 50%;
      animation: spin 0.6s linear infinite;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    .forgot-link {
      display: block;
      text-align: center;
      margin-top: 1rem;
      font-size: 0.8125rem;
      color: #3b82f6;
      text-decoration: none;
      font-weight: 500;
    }

    .forgot-link:hover {
      text-decoration: underline;
    }

    .footer {
      margin-top: 2rem;
      padding-top: 2rem;
      border-top: 1px solid #f1f5f9;
      text-align: center;
    }

    .footer-text {
      font-size: 0.6875rem;
      color: #94a3b8;
      line-height: 1.6;
    }

    .toast {
      position: fixed;
      top: 1.25rem;
      right: 1.25rem;
      z-index: 1000;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 1.25rem;
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      color: white;
      font-size: 0.875rem;
      font-weight: 500;
      animation: slideIn 0.3s ease-out;
      max-width: 320px;
    }

    .toast.error {
      background: #dc2626;
    }

    .toast.success {
      background: #059669;
    }

    @keyframes slideIn {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }

    .toast-close {
      background: none;
      border: none;
      color: white;
      cursor: pointer;
      padding: 0.125rem;
      margin-left: 0.5rem;
    }

    .toast-close:hover {
      opacity: 0.8;
    }

    /* Responsive Design */
    @media (max-width: 1024px) {
      .left-panel {
        display: none;
      }

      .right-panel {
        width: 100%;
      }
    }

    @media (max-width: 640px) {
      .right-panel {
        padding: 1rem;
      }

      .login-wrapper {
        max-width: 100%;
      }

      .login-title {
        font-size: 1.5rem;
      }

      .preview-title {
        font-size: 2rem;
      }
    }

    /* Chart Animation */
    .chart-animation {
      width: 100%;
      height: 150px;
      margin-top: 2rem;
      background: rgba(255, 255, 255, 0.03);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 12px;
      padding: 1rem;
      position: relative;
      overflow: hidden;
    }

    .chart-bars {
      position: absolute;
      bottom: 1rem;
      left: 1rem;
      right: 1rem;
      height: 80px;
      display: flex;
      align-items: flex-end;
      gap: 0.5rem;
    }

    .chart-bar {
      flex: 1;
      background: linear-gradient(to top, rgba(59, 130, 246, 0.6), rgba(139, 92, 246, 0.6));
      border-radius: 4px 4px 0 0;
      animation: growBar 2s ease-out infinite;
    }

    .chart-bar:nth-child(1) { height: 80%; animation-delay: 0s; }
    .chart-bar:nth-child(2) { height: 60%; animation-delay: 0.1s; }
    .chart-bar:nth-child(3) { height: 90%; animation-delay: 0.2s; }
    .chart-bar:nth-child(4) { height: 70%; animation-delay: 0.3s; }
    .chart-bar:nth-child(5) { height: 85%; animation-delay: 0.4s; }

    @keyframes growBar {
      0%, 100% { transform: scaleY(1); opacity: 0.6; }
      50% { transform: scaleY(1.1); opacity: 1; }
    }

    /* Welcome Screen */
    .welcome-screen {
      min-height: 100vh;
      background: #0f172a;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 1.25rem;
      position: relative;
    }

    .welcome-card {
      background: white;
      border-radius: 16px;
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
      padding: 3rem;
      text-align: center;
      max-width: 420px;
      width: 100%;
    }

    .welcome-logo {
      height: 40px;
      margin-bottom: 1.5rem;
    }

    .welcome-title {
      font-size: 2rem;
      font-weight: 700;
      color: #1e293b;
      margin-bottom: 0.5rem;
    }

    .welcome-user {
      font-size: 1rem;
      color: #64748b;
      margin-bottom: 2rem;
    }

    .success-icon {
      width: 48px;
      height: 48px;
      margin: 0 auto 1.5rem;
      color: #059669;
    }

    .logout-button {
      padding: 0.75rem 1.5rem;
      font-size: 0.875rem;
      font-weight: 600;
      border-radius: 8px;
      border: 1px solid #e2e8f0;
      background: white;
      color: #334155;
      cursor: pointer;
      transition: all 0.2s ease;
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
    }

    .logout-button:hover {
      background: #f8fafc;
      border-color: #cbd5e1;
    }
  `

  if (isLoggedIn) {
    return (
      <>
        <style>{styles}</style>
        {showToast && (
          <div className={`toast ${toastType}`}>
            {toastType === 'error' ? (
              <AlertCircle style={{ width: '20px', height: '20px' }} />
            ) : (
              <CheckCircle style={{ width: '20px', height: '20px' }} />
            )}
            <span>{toastMessage}</span>
            <button className="toast-close" onClick={() => setShowToast(false)}>
              <X style={{ width: '16px', height: '16px' }} />
            </button>
          </div>
        )}
        
        <div className="welcome-screen">
          <div className="bg-pattern"></div>
          <div className="grid-overlay"></div>
          <div className="welcome-card">
            <img 
              src="https://github.com/Sweety-Vigneshg/Vdart_Asset_Images/blob/main/vdartorginallogo.png?raw=true" 
              alt="VDart Logo"
              className="welcome-logo"
            />
            <CheckCircle className="success-icon" />
            <h1 className="welcome-title">Welcome to VDart Analytics</h1>
            <p className="welcome-user">Logged in as: {currentUser}</p>
            <button onClick={handleLogout} className="logout-button">
              <LogOut style={{ width: '16px', height: '16px' }} />
              Logout
            </button>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <style>{styles}</style>
      
      {showToast && (
        <div className={`toast ${toastType}`}>
          {toastType === 'error' ? (
            <AlertCircle style={{ width: '20px', height: '20px' }} />
          ) : (
            <CheckCircle style={{ width: '20px', height: '20px' }} />
          )}
          <span>{toastMessage}</span>
          <button className="toast-close" onClick={() => setShowToast(false)}>
            <X style={{ width: '16px', height: '16px' }} />
          </button>
        </div>
      )}

      <div className="page-container">
        <div className="bg-pattern"></div>
        <div className="grid-overlay"></div>
        
        {/* Dashboard Preview Panel */}
        <div className="left-panel">
          <div className="dashboard-preview">
            <div className="preview-header">
              <img 
                src="https://github.com/Sweety-Vigneshg/Vdart_Asset_Images/blob/main/vdartorginallogo.png?raw=true" 
                alt="VDart Logo"
                className="preview-logo"
              />
              <h1 className="preview-title">Client Satisfaction Analytics Dashboard</h1>
              <p className="preview-subtitle">
                Monitor client feedback, track satisfaction metrics, analyze survey responses, 
                and improve customer experience with real-time insights.
              </p>
            </div>

            {/* <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon blue">
                  <Users />
                </div>
                <div className="stat-value">94.2%</div>
                <div className="stat-label">Client Satisfaction Rate</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon green">
                  <TrendingUp />
                </div>
                <div className="stat-value">2,459</div>
                <div className="stat-label">Surveys Sent</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon purple">
                  <PieChart />
                </div>
                <div className="stat-value">4.8/5</div>
                <div className="stat-label">Average Rating</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon orange">
                  <Activity />
                </div>
                <div className="stat-value">87%</div>
                <div className="stat-label">Response Rate</div>
              </div>
            </div> */}

            <div className="chart-animation">
              <div className="chart-bars">
                <div className="chart-bar"></div>
                <div className="chart-bar"></div>
                <div className="chart-bar"></div>
                <div className="chart-bar"></div>
                <div className="chart-bar"></div>
              </div>
            </div>

            <div className="features">
              <div className="feature">
                <BarChart3 className="feature-icon" />
                <span>Advanced Analytics</span>
              </div>
              <div className="feature">
                <ShieldCheck className="feature-icon" />
                <span>Secure Platform</span>
              </div>
              <div className="feature">
                <Activity className="feature-icon" />
                <span>Attractive Dashboard</span>
              </div>
            </div>
          </div>
        </div>

        {/* Login Form Panel */}
        <div className="right-panel">
          <div className="login-wrapper">
            <div className="login-header">
              <h2 className="login-title">Welcome Back</h2>
              <p className="login-subtitle">Sign in to access your dashboard</p>
            </div>

            <div className="security-badge">
              <ShieldCheck style={{ width: '14px', height: '14px' }} />
              <span>256-bit SSL Encrypted</span>
            </div>

            <div className="form-group">
              <label className="label">Email Address</label>
              <div className="input-wrapper">
                <Mail className="input-icon" />
                <input
                  type="email"
                  placeholder="name@vdartinc.com"
                  value={email}
                  onChange={(e) => setEmail(sanitizeInput(e.target.value))}
                  disabled={isLocked}
                  className={`input ${error && !password ? 'error' : ''}`}
                  autoComplete="username"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="label">Password</label>
              <div className="input-wrapper">
                <Lock className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(sanitizeInput(e.target.value))}
                  disabled={isLocked}
                  className={`input ${error && password ? 'error' : ''}`}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="password-toggle"
                  tabIndex={-1}
                >
                  {showPassword ? 
                    <EyeOff style={{ width: '18px', height: '18px' }} /> : 
                    <Eye style={{ width: '18px', height: '18px' }} />
                  }
                </button>
              </div>
            </div>

            {error && (
              <div className="error-message">
                <AlertCircle style={{ width: '14px', height: '14px', flexShrink: 0 }} />
                <span>{error}</span>
              </div>
            )}

            <button
              onClick={handleLogin}
              disabled={loading || isLocked}
              className="button"
            >
              {loading ? (
                <>
                  <div className="spinner" />
                  Authenticating...
                </>
              ) : isLocked ? (
                <>
                  <Lock style={{ width: '16px', height: '16px' }} />
                  Account Locked
                </>
              ) : (
                <>
                  Access Dashboard
                  <ArrowRight style={{ width: '16px', height: '16px' }} />
                </>
              )}
            </button>

            <a href="#" className="forgot-link">
              Forgot your password?
            </a>

            <div className="footer">
              <p className="footer-text">
                This is a secure system. Unauthorized access is prohibited.
                <br />
                Session expires after 15 minutes of inactivity.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default LoginComponent